import type {
  CollectionActions,
  CollectionStateSlice,
  Ordering,
  Reference,
} from '@freelancer/datastore/core';
import {
  mergeDocuments,
  OrderByDirection,
  transformIntoDocuments,
  updateWebsocketDocuments,
} from '@freelancer/datastore/core';
import type { QuotationApi } from 'api-typings/quotations/quotations';
import { QuotationContextTypeApi } from 'api-typings/quotations/quotations';
import type { BidsCollection } from '../bids/bids.types';
import type { ProjectViewBidsCollection } from '../project-view-bids/project-view-bids.types';
import type { ProjectViewProjectsCollection } from '../project-view-projects';
import type { QuotationItemRevisionsCollection } from '../quotation-item-revisions';
import { transformQuotationItemRevision } from '../quotation-item-revisions/quotation-item-revisions.transformer';
import { transformQuotationRevision } from './quotation-revisions.transformer';
import type { QuotationRevisionsCollection } from './quotation-revisions.types';

export function quotationRevisionsReducer(
  state: CollectionStateSlice<QuotationRevisionsCollection> = {},
  action: CollectionActions<
    | QuotationRevisionsCollection
    | QuotationItemRevisionsCollection
    | BidsCollection
    | ProjectViewBidsCollection
    | ProjectViewProjectsCollection
  >,
): CollectionStateSlice<QuotationRevisionsCollection> {
  switch (action.type) {
    case 'API_UPDATE_SUCCESS': {
      if (action.payload.type === 'quotationRevisions') {
        const { originalDocument, ref, result } = action.payload;
        return updateWebsocketDocuments<QuotationRevisionsCollection>(
          state,
          [originalDocument.id],
          revision =>
            revision.id === result.quotation.quotation_version_id &&
            revision.quotationId === result.quotation.id
              ? transformQuotationRevision(result.quotation)
              : revision,
          ref,
        );
      }
      if (action.payload.type === 'quotationItemRevisions') {
        // When a quotation item is updated, the associated quotation
        // needs to be updated as well if the unit price of the item
        // has been changed.
        const { originalDocument, delta, result } = action.payload;
        const ref: Reference<QuotationRevisionsCollection> = {
          path: {
            collection: 'quotationRevisions',
            authUid: action.payload.ref.path.authUid,
          },
        };
        if (originalDocument.quotationVersionId) {
          return updateWebsocketDocuments<QuotationRevisionsCollection>(
            state,
            [originalDocument.quotationVersionId],
            revision => {
              // The item after the update.
              const newItem = transformQuotationItemRevision(
                result.quotation_item,
              );
              // Find the one with the same currency as the deleted item
              // from quotePrice.
              const changedAmount = revision.quotePrice[newItem.currencyId];

              // We only need to update the quotePrice with the same
              // currency as the updated item.
              return {
                ...revision,
                ...(delta.unitPrice && !!changedAmount
                  ? {
                      quotePrice: {
                        ...revision.quotePrice,
                        ...{
                          [newItem.currencyId]:
                            revision.quotePrice[newItem.currencyId] -
                            originalDocument.unitPrice *
                              originalDocument.quantity +
                            newItem.quantity * newItem.unitPrice,
                        },
                      },
                    }
                  : {}),
              };
            },
            ref,
          );
        }
        return state;
      }
      return state;
    }
    case 'API_PUSH_SUCCESS': {
      if (action.payload.type === 'quotationRevisions') {
        const { result, ref } = action.payload;
        return mergeDocuments<QuotationRevisionsCollection>(
          state,
          transformIntoDocuments(
            [result.quotation],
            transformQuotationRevision,
            ref.path.authUid,
          ),
          [{ field: 'id', direction: OrderByDirection.ASC }],
          ref,
        );
      }

      // Because we have a hireme quotations api now, which is used to approve
      // a quotation + create hireme project. On success for that, we need to update
      // the quotation.
      if (action.payload.type === 'projectViewProjects') {
        const { result, ref } = action.payload;
        const newRef: Reference<QuotationRevisionsCollection> = {
          path: {
            collection: 'quotationRevisions',
            authUid: ref.path.authUid,
          },
        };
        // This means that the project posted was a hireme quotation
        if (
          result.quotation_id &&
          result.quotation_version_id &&
          result.hireme &&
          result.hireme_initial_bid
        ) {
          // Get bid id to use as quotation new context.
          const bidId = result.hireme_initial_bid.id;

          // We need to approve quotation and reattach to bid context
          return updateWebsocketDocuments<QuotationRevisionsCollection>(
            state,
            [result.quotation_version_id],
            revision => ({
              ...revision,
              contextType: QuotationContextTypeApi.BID,
              contextId: bidId,
            }),
            newRef,
          );
        }
      }
      return state;
    }
    case 'API_FETCH_SUCCESS': {
      if (action.payload.type === 'quotationRevisions') {
        const { result, ref, order } = action.payload;
        return mergeDocuments<QuotationRevisionsCollection>(
          state,
          transformIntoDocuments(
            result.quotations,
            transformQuotationRevision,
            ref.path.authUid,
          ),
          order,
          ref,
        );
      }
      // Add quotations obtained via a projection on bids GET
      // requests to the collection
      if (
        action.payload.type === 'bids' ||
        action.payload.type === 'projectViewBids'
      ) {
        const bids = action.payload.result.bids ?? [];
        const quotations: readonly QuotationApi[] = bids.reduce(
          (quotes, bid) =>
            bid.quotations ? [...quotes, ...bid.quotations] : quotes,
          [] as QuotationApi[],
        );

        const ref: Reference<QuotationRevisionsCollection> = {
          path: {
            collection: 'quotationRevisions',
            authUid: action.payload.ref.path.authUid,
          },
        };
        const order: Ordering<QuotationRevisionsCollection> = [
          { field: 'id', direction: OrderByDirection.ASC },
        ];
        return mergeDocuments<QuotationRevisionsCollection>(
          state,
          transformIntoDocuments(
            quotations,
            transformQuotationRevision,
            ref.path.authUid,
          ),
          order,
          ref,
        );
      }

      return state;
    }
    case 'API_DELETE_SUCCESS': {
      if (action.payload.type === 'quotationItemRevisions') {
        // When one of the quotation item gets deleted, we need
        // to update the associated quotation as well, as the
        // deletion caused the change in quote price.
        const { originalDocument } = action.payload;
        const ref: Reference<QuotationRevisionsCollection> = {
          path: {
            collection: 'quotationRevisions',
            authUid: action.payload.ref.path.authUid,
          },
        };
        if (originalDocument.quotationVersionId) {
          return updateWebsocketDocuments<QuotationRevisionsCollection>(
            state,
            [originalDocument.quotationVersionId],
            revision => {
              // Find the one with the same currency as the deleted item
              // from quotePrice.
              const changedAmount =
                revision.quotePrice[originalDocument.currencyId];
              // We only need to take the deleted item price off from
              // the quotePrice item with the same currency.
              return {
                ...revision,
                ...(changedAmount
                  ? {
                      quotePrice: {
                        ...revision.quotePrice,
                        ...{
                          [originalDocument.currencyId]:
                            revision.quotePrice[originalDocument.currencyId] -
                            originalDocument.unitPrice *
                              originalDocument.quantity,
                        },
                      },
                    }
                  : {}),
              };
            },
            ref,
          );
        }
      }
      return state;
    }
    default:
      return state;
  }
}
